import { useState, useCallback, useMemo } from 'react';

export type EditingTarget = 'reactant' | 'product' | 'molecule' | null;

interface UseSketcherModalProps {
  reactantConfig: string;
  productConfig: string;
  setReactantConfig: (config: string) => void;
  setProductConfig: (config: string) => void;
}

export function useSketcherModal({
  reactantConfig,
  productConfig,
  setReactantConfig,
  setProductConfig,
}: UseSketcherModalProps) {
  const [editingTarget, setEditingTarget] = useState<EditingTarget>(null);
  const [loadExisting, setLoadExisting] = useState(false);

  // Open sketcher for reactant (blank)
  const editReactant = useCallback(() => {
    setEditingTarget('reactant');
    setLoadExisting(false);
  }, []);

  // Open sketcher for product (blank)
  const editProduct = useCallback(() => {
    setEditingTarget('product');
    setLoadExisting(false);
  }, []);

  // Open sketcher for molecule (blank)
  const editMolecule = useCallback(() => {
    setEditingTarget('molecule');
    setLoadExisting(false);
  }, []);

  // Open sketcher for reactant with existing config
  const editReactantExisting = useCallback(() => {
    setEditingTarget('reactant');
    setLoadExisting(true);
  }, []);

  // Open sketcher for product with existing config
  const editProductExisting = useCallback(() => {
    setEditingTarget('product');
    setLoadExisting(true);
  }, []);

  // Open sketcher for molecule with existing config
  const editMoleculeExisting = useCallback(() => {
    setEditingTarget('molecule');
    setLoadExisting(true);
  }, []);

  // Close sketcher
  const close = useCallback(() => {
    setEditingTarget(null);
  }, []);

  // Save configuration from sketcher
  const save = useCallback((config: string) => {
    if (editingTarget === 'reactant' || editingTarget === 'molecule') {
      setReactantConfig(config);
    } else if (editingTarget === 'product') {
      setProductConfig(config);
    }
  }, [editingTarget, setReactantConfig, setProductConfig]);

  // Computed values for sketcher overlay
  const initialConfig = useMemo(() => {
    if (!loadExisting) return '';
    return editingTarget === 'product' ? productConfig : reactantConfig;
  }, [editingTarget, reactantConfig, productConfig, loadExisting]);

  const label = useMemo(() => {
    if (editingTarget === 'molecule') return 'Molecule';
    if (editingTarget === 'reactant') return 'Reactant';
    if (editingTarget === 'product') return 'Product';
    return '';
  }, [editingTarget]);

  return {
    // State
    editingTarget,
    /** `false` when the user opened Build Structure (blank canvas); `true` when editing existing. */
    loadExisting,

    // Functions - blank canvas
    editReactant,
    editProduct,
    editMolecule,

    // Functions - load existing
    editReactantExisting,
    editProductExisting,
    editMoleculeExisting,

    close,
    save,

    // Computed values
    initialConfig,
    label,

    // For SketcherOverlay component
    open: editingTarget !== null,
  };
}